/**
 * Shared avatar image utilities used by AdminProfile and UserProfile.
 */

const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5 MB
const MAX_DIMENSION = 400; // px
const JPEG_QUALITY = 0.8;

/**
 * Validate an image file for type and size.
 * @param {File} file
 * @returns {{ valid: boolean, error?: string }}
 */
export function validateImageFile(file) {
  if (!file.type.startsWith('image/')) {
    return { valid: false, error: 'Please select a valid image file.' };
  }
  if (file.size > MAX_FILE_SIZE) {
    return {
      valid: false,
      error: 'Image file is too large. Please select an image smaller than 5MB.',
    };
  }
  return { valid: true };
}

/**
 * Read, resize and compress an image file to a base64 JPEG data-URL.
 * @param {File} file
 * @param {{ maxDimension?: number, quality?: number }} [opts]
 * @returns {Promise<string>} compressed base64 data URL
 */
export function compressImage(file, { maxDimension = MAX_DIMENSION, quality = JPEG_QUALITY } = {}) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('Failed to read file.'));
    reader.onload = (e) => {
      const img = new Image();
      img.onerror = () => reject(new Error('Failed to load image.'));
      img.onload = () => {
        const canvas = document.createElement('canvas');
        let { width, height } = img;

        if (width > maxDimension || height > maxDimension) {
          if (width > height) {
            height = (height / width) * maxDimension;
            width = maxDimension;
          } else {
            width = (width / height) * maxDimension;
            height = maxDimension;
          }
        }

        canvas.width = width;
        canvas.height = height;
        canvas.getContext('2d').drawImage(img, 0, 0, width, height);

        resolve(canvas.toDataURL('image/jpeg', quality));
      };
      img.src = e.target.result;
    };
    reader.readAsDataURL(file);
  });
}
